import React, { useMemo, useRef, useState } from 'react';
import { TouchTargetBehavior } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { FileRejection } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { IsAliveFn } from 'apps/sigma2-ui/src/lib/hooks/useAlive';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useUserUploadHeadless } from 'apps/sigma2-ui/src/users/lib/useUserUploadHeadless';

export interface UserUploadModalHook {
  modalTitle: string;
  modalText: string;
  uploadFileButtonBehavior: TouchTargetBehavior;
  uploadFileButtonLabel: string;
  uploadFileButtonDisabled: boolean;
  errorMessage: string | null;
  selectedFileMessage: string | null;
  dropzone: React.ReactNode;
}
export const useUserUploadModal = ({
  onComplete,
  userId,
}: {
  onComplete: (userUploadId: string) => void;
  userId: string;
}) => {
  const { myUserStatus } = useUser();

  const [dropzoneFile, setDropzoneFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dropzoneError, setDropzoneError] = useState<string | null>(null);
  const [showError, setShowError] = useState<boolean>(false);
  const [uploadInProgress, setUploadInProgress] = useState(false);

  const { canUpload, upload, userUploadId, presignedUrl } =
    useUserUploadHeadless({ userId });

  const error = useMemo(() => {
    if (dropzoneError) {
      return dropzoneError;
    }

    return uploadError;
  }, [dropzoneError, uploadError]);

  const onDrop = useAsyncCallback(
    async (
      isAlive: IsAliveFn,
      acceptedFiles: File[],
      rejectedFiles: FileRejection[],
    ) => {
      if (!isAlive() || uploadInProgress) {
        return;
      }

      if (rejectedFiles.length >= 2 || acceptedFiles.length > 1) {
        setDropzoneFile(null);
        setDropzoneError('Please only upload one file at a time');
        return;
      }

      if (rejectedFiles.length > 0) {
        setDropzoneFile(null);
        setDropzoneError('Please upload a valid file type');
        return;
      }

      setDropzoneError(null);
      setDropzoneFile(acceptedFiles[0]);
    },
    [uploadInProgress],
  );

  const handleUpload = useAsyncCallback(
    async (isAlive) => {
      if (
        !isAlive() ||
        myUserStatus !== 'logged_in' ||
        !dropzoneFile ||
        !upload
      ) {
        return;
      }

      setUploadInProgress(true);
      setShowError(false);

      upload(
        dropzoneFile,
        (userUploadId) => {
          onComplete(userUploadId);
          setUploadInProgress(false);
        },
        (error) => {
          setShowError(true);
          setUploadError(error);
          setUploadInProgress(false);
        },
      );
    },
    [dropzoneFile, myUserStatus, onComplete, upload],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      'text/csv': ['.csv'],
      'application/json': ['.json'],
      'application/vnd.ms-excel': ['.xls', '.xlsx'],
      'application/zip': ['.zip'],
    },
  });

  const uploadDropzoneRef = useRef<HTMLInputElement>(null);

  return useMemo(() => {
    const hook: UserUploadModalHook = {
      modalTitle: 'Upload File',
      modalText: 'Please select a file to upload',
      uploadFileButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          if (!canUpload || uploadInProgress) {
            return;
          }
          handleUpload();
        },
      },
      uploadFileButtonLabel: 'Upload File',
      uploadFileButtonDisabled:
        !dropzoneFile || uploadInProgress || presignedUrl === null,
      errorMessage: showError ? error : null,
      selectedFileMessage: dropzoneFile
        ? `Selected File: ${dropzoneFile.name}`
        : null,
      dropzone: (
        <div
          {...getRootProps()}
          style={{ border: '3px dashed rgb(106, 110, 113)' }}
          className="w-full h-32 flex items-center justify-center"
          data-qa="fileDropzone"
        >
          <input
            {...getInputProps({})}
            data-user-upload-id={userUploadId ?? ''}
            data-user-upload-ready={canUpload ? 'true' : 'false'}
            ref={uploadDropzoneRef}
            className="form-input"
          />

          <button
            onClick={() => uploadDropzoneRef.current?.click()}
            type="button"
          >
            <span>
              Drag and drop your file here, or click to select your file.
            </span>
          </button>
        </div>
      ),
    };

    return hook;
  }, [
    dropzoneFile,
    uploadInProgress,
    presignedUrl,
    showError,
    error,
    getRootProps,
    getInputProps,
    userUploadId,
    canUpload,
    handleUpload,
  ]);
};
